import type { DataProperty, RestrictedObjectProperty } from '@semantical/modules/ontology-model';

export function isRequiredDataProperty(prop: DataProperty): boolean {
    if (!prop.cardinality) return false;
    if (prop.cardinality.exact !== undefined && prop.cardinality.exact >= 1) return true;
    if (prop.cardinality.min !== undefined && prop.cardinality.min >= 1) return true;
    return false;
}

export function isOptionalDataProperty(prop: DataProperty): boolean {
    if (!prop.cardinality) return true;
    if (prop.cardinality.exact !== undefined && prop.cardinality.exact === 0) return true;
    if (prop.cardinality.min !== undefined && prop.cardinality.min === 0) return true;
    if (prop.cardinality.min === undefined && prop.cardinality.exact === undefined) return true;
    return false;
}

export function isRequiredObjectPropertyRestriction(restriction: RestrictedObjectProperty): boolean {
    return restriction.cardinality.min >= 1;
}

export function isOptionalObjectPropertyRestriction(restriction: RestrictedObjectProperty): boolean {
    return restriction.cardinality.min === 0;
}

export function getInputType(range: string): string {
    switch (range.toLowerCase()) {
        case 'date':
        case 'datetime':
            return 'date';
        case 'integer':
        case 'int':
        case 'long':
        case 'short':
            return 'number';
        case 'decimal':
        case 'float':
        case 'double':
            return 'number';
        case 'boolean':
            return 'checkbox';
        default:
            return 'text';
    }
}

export function getInputStep(range: string): string | undefined {
    switch (range.toLowerCase()) {
        case 'decimal':
        case 'float':
        case 'double':
            return '0.01';
        case 'integer':
        case 'int':
        case 'long':
        case 'short':
            return '1';
        default:
            return undefined;
    }
}

export function formatDataPropertyCardinality(prop: DataProperty): string {
    if (!prop.cardinality) return ' [0..*]';
    if (prop.cardinality.exact !== undefined) {
        // Hide [1] as it's the default expected cardinality
        if (prop.cardinality.exact === 1) return '';
        return ` [${prop.cardinality.exact}]`;
    }
    if (prop.cardinality.min !== undefined || prop.cardinality.max !== undefined) {
        const min = prop.cardinality.min ?? 0;
        const max = prop.cardinality.max ?? '*';
        // Hide [1..1] as it's the default expected cardinality
        if (min === 1 && max === 1) return '';
        return ` [${min}..${max}]`;
    }
    return ' [0..*]';
}

export function formatObjectPropertyCardinality(restriction: RestrictedObjectProperty): string {
    const min = restriction.cardinality.min;
    const max = restriction.cardinality.max;
    // Hide [1..1] as it's the default expected cardinality
    if (min === 1 && max === 1) return '';
    return ` [${min}..${max}]`;
}
